import {observer} from 'mobx-react-lite'
import React, {
  CSSProperties,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react'
import {Lang} from 'yougile-lang'
import {Time} from 'yougile-util'
import {Backend} from '@common/backend'
import {ButtonNew, LinkButtonNew} from '@components/common/base/buttons'
import {Dialog} from '@components/common/base/dialog/dialog'
import {InputNew} from '@components/common/base/input-new'
import {PhoneInput} from '@components/common/base/input-new/phone-input'
import {
  TwAttentionDescription,
  TwDialogDescription,
  TwError,
  TwTwoFactorStepInner,
  TwTwoFactorStepWrapper
} from '@components/login/containers'
import {cn} from '@containers/html-utils'
import {globals} from '@extensions/globals-base'
import {LoginDesktopServer} from './login-desktop-server'
import {LoginRecovery} from './login-recovery'
import {useLoginStore} from './login-store'
import {OpenIdSignIn} from './openid-sign-in'

const TwoFactorStepCodeCheck = observer(() => {
  const [state, store] = useLoginStore()
  const [code, setCode] = useState('')
  const [timeLeft, setTimeLeft] = useState<number>()
  const targetTime = useRef<number>(0)

  useEffect(() => {
    if (code.length === 6) {
      store.submitPhoneVerificationCode(code)
    }
  }, [code.length])

  useEffect(() => {
    if (!state.retryTimestamp) {
      return () => void 0
    }

    targetTime.current = state.retryTimestamp
      ? new Date(state.retryTimestamp).getTime() + 60000
      : 0

    const interval = setInterval(() => {
      const currentTime = Time.now()
      const difference = Math.max(
        0,
        Math.floor((targetTime.current - currentTime) / 1000)
      )

      setTimeLeft(difference)

      if (difference === 0) {
        clearInterval(interval)
      }
    }, 1000)

    return () => clearInterval(interval)
  }, [state.retryTimestamp])

  const formatTime = (seconds: number): string => {
    return `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(
      seconds % 60
    ).padStart(2, '0')}`
  }

  const onChangeCode = (value: string) => {
    setCode(value)
    if (state.codeError) {
      store.setCodeError()
    }
  }

  const submitCode = () => {
    store.submitPhoneVerificationCode(code)
  }

  const resendCode = () => {
    setTimeLeft(undefined)
    store.submit({
      resendCode: true
    })
    onChangeCode('')
  }

  return (
    <TwTwoFactorStepWrapper>
      <Dialog
        hidePrimaryBtn
        inlineDisplay
        close={store.closeTwoFactorStep}
        closeBtnText={!state.isPhoneSetByUser ? Lang.msg.back : Lang.msg.cancel}
        submit={submitCode}
        width={415}
        title={Lang.msg.users.twoFactorAuthentication}
      >
        <TwTwoFactorStepInner>
          {state.twoFactorCodeSentTo && (
            <TwDialogDescription>
              {Lang.msg.users.codeSentTo(state.twoFactorCodeSentTo)}
              {state.isPhoneSetByUser && (
                <LinkButtonNew
                  onClick={() => {
                    store.setCodeError()
                    store.setTwoFactorStep('phone')
                  }}
                >
                  {Lang.msg.change}
                </LinkButtonNew>
              )}
            </TwDialogDescription>
          )}
          <InputNew
            autoFocus
            mask="999999"
            inputClassName="!text-left"
            label={Lang.msg.users.codeFromSms}
            rightLabel={
              !timeLeft &&
              timeLeft !== undefined &&
              state.retryTimestamp && (
                <LinkButtonNew onClick={resendCode}>
                  {Lang.msg.users.resendCode}
                </LinkButtonNew>
              )
            }
            support={
              <>
                {state.codeError && <TwError>{state.codeError}</TwError>}
                {!!timeLeft && (
                  <>{Lang.msg.users.sendAgainIn(formatTime(timeLeft))}</>
                )}
              </>
            }
            value={code}
            onChange={onChangeCode}
            error={Boolean(state.codeError)}
            rightIconClassName="animate-spin-slow"
            iconRight={
              state.isPendingTwoFactor ? 'IconCustomBlueLoader' : undefined
            }
          />
        </TwTwoFactorStepInner>
      </Dialog>
    </TwTwoFactorStepWrapper>
  )
})

const TwoFactorPhoneStep = observer(() => {
  const [state, store] = useLoginStore()
  const [phoneNumber, setPhoneNumber] = useState<string>()

  const onChangePhoneNumber = useCallback(
    ({value, isInvalid}: {value: string; isInvalid: boolean}) => {
      store.setPhoneIsInvalid(isInvalid)
      store.setPhoneError(undefined)
      setPhoneNumber(value)
    },
    []
  )

  const submitPhoneNumber = () => {
    if (phoneNumber) {
      void store.startPhoneVerification(phoneNumber)
    }
  }

  const onPhoneKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !state.phoneIsInvalid) {
      submitPhoneNumber()
    }
  }

  return (
    <TwTwoFactorStepWrapper>
      <Dialog
        inlineDisplay
        close={store.closeTwoFactorStep}
        submit={submitPhoneNumber}
        width={415}
        primaryBtnDisabled={state.phoneIsInvalid || state.isPendingTwoFactor}
        title={Lang.msg.users.twoFactorAuthentication}
        primaryBtnText={Lang.msg.users.acceptBtn}
      >
        <TwTwoFactorStepInner>
          <TwDialogDescription>
            {Lang.msg.users.twoFactorDescription}
          </TwDialogDescription>
          <PhoneInput
            autoFocus
            onChange={onChangePhoneNumber}
            onKeyDown={onPhoneKeyDown}
            invalid={state.phoneIsInvalid}
            support={
              state.phoneError ? (
                <TwError>{state.phoneError}</TwError>
              ) : undefined
            }
            iconRight={
              state.isPendingTwoFactor ? 'IconCustomBlueLoader' : undefined
            }
            rightIconClassName="animate-spin-slow"
          />
        </TwTwoFactorStepInner>
      </Dialog>
    </TwTwoFactorStepWrapper>
  )
})

const TwoFactorBlocked = observer(() => {
  const [, store] = useLoginStore()

  return (
    <TwTwoFactorStepWrapper>
      <Dialog
        type="one-button"
        inlineDisplay
        submit={store.closeTwoFactorStep}
        close={store.closeTwoFactorStep}
        width={410}
        title={Lang.msg.users.twoFactorAuthentication}
        primaryBtnText={Lang.msg.close}
      >
        <TwTwoFactorStepInner>
          <TwAttentionDescription>
            {Lang.msg.users.twoFactorBlocked}
          </TwAttentionDescription>
        </TwTwoFactorStepInner>
      </Dialog>
    </TwTwoFactorStepWrapper>
  )
})

const TwoFactorSuccess = observer(() => {
  const [, store] = useLoginStore()

  return (
    <TwTwoFactorStepWrapper>
      <Dialog
        type="one-button"
        inlineDisplay
        submit={store.saveCredsAndRefresh}
        close={store.saveCredsAndRefresh}
        width={410}
        title={Lang.msg.users.twoFactorAuthenticationSuccess}
        primaryBtnText={Lang.msg.finish}
      />
    </TwTwoFactorStepWrapper>
  )
})

export const LoginWindow = observer(() => {
  const [state, store] = useLoginStore()

  const containerStyle: CSSProperties = state.isDesktop
    ? {
        top: '160px',
        paddingBottom: '0px'
      }
    : {}

  if (state.twoFactorStep === 'code') {
    return <TwoFactorStepCodeCheck />
  }

  if (state.twoFactorStep === 'phone') {
    return <TwoFactorPhoneStep />
  }

  if (state.twoFactorStep === 'blocked') {
    return <TwoFactorBlocked />
  }

  if (state.twoFactorStep === 'success') {
    return <TwoFactorSuccess />
  }
  if (Backend.initResponse?.openId?.allowOnlyOpenId) {
    return (
      <div className="login-wnd h-240 flex flex-col items-center justify-center">
        <div className="login-title">{Lang.msg.signIn3}:</div>
        <OpenIdSignIn />
      </div>
    )
  }

  return (
    <div className="login-wnd" style={containerStyle}>
      <a
        href="/"
        onClick={store.goToMainSite}
        className="login-wnd-logo -top-128"
      />
      {state.company && (
        <div className="absolute text-center -top-64 w-full text-20 font-semibold text-secondary-white">
          {Lang.msg.common.company} “{state.company.name}”
        </div>
      )}
      <div className="login-title">{Lang.msg.signIn3}:</div>
      <form className="login-form">
        <label className="login-form-field">
          <span className="login-form-label">{Lang.msg.email}:</span>
          <input
            className="login-form-input _input"
            type="email"
            value={state.loginFormEmail}
            onChange={store.setLoginFormEmail}
            disabled={state.loginFormWait}
            onKeyDown={store.keyDown}
            placeholder={Lang.msg.emailPlaceholder}
            autoComplete="email"
          />
        </label>
        <label className="login-form-field">
          <span className="login-form-label">{Lang.msg.password}:</span>
          <input
            className="login-form-input _input"
            type="password"
            value={state.loginFormPassword}
            onChange={store.setLoginFormPassword}
            disabled={state.loginFormWait}
            onKeyDown={store.keyDown}
            placeholder={Lang.msg.passwordPlaceholder}
            autoComplete="current-password"
          />
        </label>
        {state.isDesktop && (
          <label className="login-remember">
            <input
              type="checkbox"
              className="_checkbox"
              checked={state.loginFormRemember}
              onClick={store.toggleRemember}
            />
            <span className="_checkbox" />
            {Lang.msg.rememberAuth}
          </label>
        )}
        <ButtonNew
          className="m-auto !rounded-4"
          onClick={() => store.submit()}
          pending={state.loginFormWait}
          style={{width: '250px'}}
        >
          {Lang.msg.signIn3}
        </ButtonNew>
        {!!state.loginFormError && (
          <div className="login-error">
            <i className="fi-alert" /> {state.loginFormError}
          </div>
        )}
      </form>

      <div className="login__social-cnt hidden" ref={store.initLoginSocials}>
        <div className="login__or">{Lang.msg.common.or}</div>
        <div className="s-reg-google" />
      </div>

      {store.isDesktopWithSocialButtons() && (
        <div className={cn((state.serverShow || state.forgetShow) && 'hidden')}>
          <div className="login__or">{Lang.msg.common.or}</div>
          <div
            className="login__google-cnt"
            ref={store.renderDesktopSocialButton}
          />
        </div>
      )}

      {!globals.disableEmailRecovery && <LoginRecovery />}

      {state.company && !state.company?.regEmail && (
        <div className="_link mt-8" onClick={store.companyBack}>
          {Lang.msg.back}
        </div>
      )}

      <OpenIdSignIn />
      {state.isDesktop && <LoginDesktopServer />}
    </div>
  )
})
